import { useState, useEffect } from 'react';
import {
    Search, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Activity, Calendar, AlertTriangle, Info, AlertCircle, CheckCircle2,
    ArrowUp, ArrowDown, ArrowUpDown, Loader2, Server, Clock, Tag, Shield,
    Bot, Ticket, CircleDot,
    SquareArrowOutUpRight, X, XCircle,
    Download, Eye
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import { useSearchParams } from 'react-router-dom';
import { useClientLevel } from '../../hooks/useClientLevel';
import SingleSelectLocalSearchApiDropdown from '../../components/SingleSelectLocalSearchApiDropdown';
import AgentExportModal from '../Agent/AgentExportModal';
import LocationSelectorSection, { type LocationIds } from '../../components/LocationSelectorSection';


// --- Interfaces ---
interface EventItem {
    id: number;
    event_type: string;
    severity: string;
    message: string;
    description: string;
    event_status: string;
    asset_name: string;
    event_data: string;
    organization_id: number;
    client_id: number;
    asset_id: number | null;
    agent_id: number;
    asset_hostname: string | null;
    asset_ip: string | null;
    agent_name: string;
    client_name: string;
    ticket_id: number | null;
    ticket_number: string | null;
    ticket_url?: string | null;
    created: string;
    updated: string;
    is_deleted: boolean;
    [key: string]: any;
}


// --- Helper Functions ---
const getSeverityConfig = (severity: string) => {
    const s = severity?.toLowerCase() || '';
    if (s === 'critical') return { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-100', icon: AlertTriangle, dot: 'bg-red-500' };
    if (s === 'high' || s === 'error') return { color: 'text-[#422568]', bg: 'bg-[#422568]', border: 'border-[#422568]', icon: AlertCircle, dot: 'bg-[#422568]' };
    if (s === 'medium' || s === 'warning') return { color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-100', icon: AlertCircle, dot: 'bg-yellow-500' };
    if (s === 'low' || s === 'info') return { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100', icon: Info, dot: 'bg-blue-500' };
    if (s === 'success') return { color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100', icon: CheckCircle2, dot: 'bg-green-500' };
    return { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-100', icon: Info, dot: 'bg-slate-500' };
};

const getStatusConfig = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s === 'open') return { color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-100', dot: 'bg-yellow-500' };
    if (s === 'closed') return { color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100', dot: 'bg-green-500' };
    return { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-100', dot: 'bg-slate-500' };
};

const formatTimestamp = (ts: string) => {
    if (!ts) return '-';
    try {
        const date = new Date(ts);
        return date.toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
        });
    } catch { return ts; }
};

const getRelativeTime = (ts: string) => {
    if (!ts) return '';
    try {
        const now = new Date();
        const date = new Date(ts);
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 30) return `${diffDays}d ago`;
        return `${Math.floor(diffDays / 30)}mo ago`;
    } catch { return ''; }
};

const formatEventType = (type: string) => {
    if (!type) return '-';
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};


// --- Main Component ---
const EventsTable = ({ clientId }: { clientId?: number }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const statusFilter = (searchParams.get('event_status') as 'all' | 'open' | 'closed') || 'all';
    const severityFilter = (searchParams.get('severity') as 'all' | 'critical' | 'warning' | 'info') || 'all';
    const datePreset = (searchParams.get('date_preset') as 'all' | '24h' | '7d' | '30d' | 'custom') || 'all';
    const dateFrom = searchParams.get('from_date') || '';
    const dateTo = searchParams.get('to_date') || '';
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showExportModal, setShowExportModal] = useState(false);
    const [eventsData, setEventsData] = useState<EventItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(() => {
        const p = searchParams.get('page');
        return p ? parseInt(p, 10) : 1;
    });
    const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
        const l = searchParams.get('limit');
        return l ? parseInt(l, 10) : 30;
    });
    const [sortBy, setSortBy] = useState<string>(() => searchParams.get('sort_by') || 'created');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc');
    const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
    const [eventTypeOption, setEventTypeOption] = useState<any | null>(null);
    const [closeModalOpen, setCloseModalOpen] = useState(false);
    const [closeEventId, setCloseEventId] = useState<number | null>(null);
    const [closeEventName, setCloseEventName] = useState<string>('');
    const [closeReason, setCloseReason] = useState('');
    const [closingEvent, setClosingEvent] = useState(false);
    const [activeSnapshotUrl, setActiveSnapshotUrl] = useState<string | null>(null);
    const [expandedDescriptions, setExpandedDescriptions] = useState<Record<number, boolean>>({});

    const toggleDescription = (eventId: number) => {
        setExpandedDescriptions(prev => ({
            ...prev,
            [eventId]: !prev[eventId]
        }));
    };
    const [locationIds, setLocationIds] = useState<LocationIds>({
        regionId: 2,
        stateId: 1,
        districtId: null,
        townId: null,
    });




    const handleStatusFilterChange = (val: 'all' | 'open' | 'closed') => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val === 'all') next.delete('event_status');
            else next.set('event_status', val);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const handleSeverityFilterChange = (val: 'all' | 'critical' | 'warning' | 'info') => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val === 'all') next.delete('severity');
            else next.set('severity', val);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const handleEventTypeChange = (val: any | null) => {
        setEventTypeOption(val);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (!val || val.event_type === 'all') next.delete('event_type');
            else next.set('event_type', val.event_type);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };


    const handleDateChange = (type: 'from_date' | 'to_date', val: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val) next.set(type, val);
            else next.delete(type);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const clearDateFilters = () => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('from_date');
            next.delete('to_date');
            next.delete('date_preset');
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const handleDatePresetChange = (val: 'all' | '24h' | '7d' | '30d' | 'custom') => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val === 'all') next.delete('date_preset');
            else next.set('date_preset', val);

            if (val !== 'custom') {
                next.delete('from_date');
                next.delete('to_date');
            }
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const level = useClientLevel();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;



    const handleLocationChange = (newIds: LocationIds) => {
        setLocationIds(newIds);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (newIds.regionId) next.set('region_id', String(newIds.regionId)); else next.delete('region_id');
            if (newIds.stateId) next.set('state_id', String(newIds.stateId)); else next.delete('state_id');
            if (newIds.districtId) next.set('district_id', String(newIds.districtId)); else next.delete('district_id');
            if (newIds.townId) next.set('town_id', String(newIds.townId)); else next.delete('town_id');
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    // Sync from URL params
    useEffect(() => {
        const p = searchParams.get('page');
        const l = searchParams.get('limit');
        const sb = searchParams.get('sort_by');
        const so = searchParams.get('sort_order');
        const et = searchParams.get('event_type');
        const rid = searchParams.get('region_id');
        const sid = searchParams.get('state_id');
        const did = searchParams.get('district_id');
        const tid = searchParams.get('town_id');

        if (p) {
            const pageNum = parseInt(p, 10);
            if (!isNaN(pageNum) && pageNum !== currentPage) setCurrentPage(pageNum);
        }
        if (l) {
            const limitNum = parseInt(l, 10);
            if (!isNaN(limitNum) && limitNum !== itemsPerPage) setItemsPerPage(limitNum);
        }
        if (sb && sb !== sortBy) setSortBy(sb);
        if (so && (so === 'asc' || so === 'desc') && so !== sortOrder) setSortOrder(so as 'asc' | 'desc');

        if (et) {
            if (!eventTypeOption || eventTypeOption.event_type !== et) {
                setEventTypeOption({ event_type: et, label: formatEventType(et) });
            }
        } else if (eventTypeOption) {
            setEventTypeOption(null);
        }

        // Sync location IDs
        setLocationIds(prev => {
            const next = {
                regionId: rid ? (isNaN(Number(rid)) ? rid : Number(rid)) : 2,
                stateId: sid ? (isNaN(Number(sid)) ? sid : Number(sid)) : 1,
                districtId: did ? (isNaN(Number(did)) ? did : Number(did)) : null,
                townId: tid ? (isNaN(Number(tid)) ? tid : Number(tid)) : null,
            };
            if (JSON.stringify(prev) !== JSON.stringify(next)) return next;
            return prev;
        });
    }, [searchParams]);



    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
                setSearchParams(prev => {
                    const next = new URLSearchParams(prev);
                    next.set('page', '1');
                    return next;
                }, { replace: true });
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);


    // Fetch data
    const fetchData = async (skip: number = 0, limit: number = itemsPerPage) => {
        try {
            setLoading(true);
            const queryObj: any = level?.id ? { client_id: level.id } : {};
            if (statusFilter !== 'all') queryObj.event_status = statusFilter;
            if (eventTypeOption?.event_type) queryObj.event_type = eventTypeOption.event_type;
            if (clientId) queryObj.client_id = clientId;
            if (severityFilter !== 'all') queryObj.severity = severityFilter;

            if (locationIds.regionId) queryObj.region_id = locationIds.regionId;
            if (locationIds.stateId) queryObj.state_id = locationIds.stateId;
            if (locationIds.districtId) queryObj.district_id = locationIds.districtId;
            if (locationIds.townId) queryObj.town_id = locationIds.townId;

            if (debouncedSearch) {
                queryObj.$or = [
                    { message: { $ilike: `%${debouncedSearch}%` } },
                    { description: { $ilike: `%${debouncedSearch}%` } },
                    { event_type: { $ilike: `%${debouncedSearch}%` } },
                    { asset_hostname: { $ilike: `%${debouncedSearch}%` } },
                    { asset_ip: { $ilike: `%${debouncedSearch}%` } },
                    { agent_name: { $ilike: `%${debouncedSearch}%` } },
                    { ticket_number: { $ilike: `%${debouncedSearch}%` } }
                ];
            }

            if (datePreset === '24h') {
                const d = new Date();
                d.setHours(d.getHours() - 24);
                queryObj.created = { $between: [d.toISOString(), new Date().toISOString()] };
            } else if (datePreset === '7d') {
                const d = new Date();
                d.setDate(d.getDate() - 7);
                queryObj.created = { $between: [d.toISOString(), new Date().toISOString()] };
            } else if (datePreset === '30d') {
                const d = new Date();
                d.setDate(d.getDate() - 30);
                queryObj.created = { $between: [d.toISOString(), new Date().toISOString()] };
            } else if (datePreset === 'custom') {
                if (dateFrom || dateTo) {
                    let startStr = "1970-01-01T00:00:00.000Z";
                    let endStr = new Date().toISOString();
                    if (dateFrom) {
                        const parsedStart = new Date(`${dateFrom}T00:00:00`);
                        if (!isNaN(parsedStart.getTime())) {
                            startStr = parsedStart.toISOString();
                        }
                    }
                    if (dateTo) {
                        const parsedEnd = new Date(`${dateTo}T23:59:59.999`);
                        if (!isNaN(parsedEnd.getTime())) {
                            endStr = parsedEnd.toISOString();
                        }
                    }
                    queryObj.created = { $between: [startStr, endStr] };
                }
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip, limit,
                sort_by: sortBy, sort_order: sortOrder,
            };

            const response = await baseapi.get('/events', { params });
            if (response.data.status) {
                setEventsData(response.data.data);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
            } else {
                setEventsData([]); setTotalPages(0); setTotalItems(0);
            }
        } catch (error: any) { showError(error.message); }
        finally { setLoading(false); }
    };


    useEffect(() => {
        fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [currentPage, itemsPerPage, debouncedSearch, level, sortBy, sortOrder, statusFilter, severityFilter, dateFrom, dateTo, datePreset, eventTypeOption, locationIds]);


    const handleRefresh = () => fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);


    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
            }, { replace: true });
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(1);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('limit', String(value));
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const handleSort = (field: string) => {
        let nextOrder: 'asc' | 'desc' = 'asc';
        if (sortBy === field) {
            nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSortBy(field);
        setSortOrder(nextOrder);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('sort_by', field);
            next.set('sort_order', nextOrder);
            return next;
        }, { replace: true });
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-slate-400 transition-opacity" />;
        return sortOrder === 'asc'
            ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-[#422568]" />
            : <ArrowDown className="w-2.5 h-2.5 ml-1 text-[#422568]" />;
    };

    const handleCloseEventClick = (event: EventItem) => {
        setCloseEventId(event.id);
        setCloseEventName(event.asset_hostname || event.agent_name || `Event #${event.id}`);
        setCloseReason('');
        setCloseModalOpen(true);
    };

    const confirmCloseEvent = async () => {
        if (!closeEventId || !closeReason.trim()) return;
        try {
            setClosingEvent(true);
            const res = await baseapi.put(`/events/${closeEventId}/close`, { reason: closeReason.trim() });
            if (res.data.status) {
                showSuccess('Event closed successfully');
                handleRefresh();
            } else {
                showError(res.data.message || 'Failed to close event');
            }
        } catch (error: any) {
            showError(error.message || 'Failed to close event');
        } finally {
            setClosingEvent(false);
            setCloseModalOpen(false);
            setCloseEventId(null);
            setCloseReason('');
        }
    };


    return (
        <div className="bg-white p-3 pb-20">
            <div className="w-full mx-auto">
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-[#422568] p-1.5 rounded shadow-lg"><Activity className="w-4 h-4 text-white" /></div>
                        <div>
                            <h1 className="font-bold text-lg text-slate-800 leading-none">Events</h1>
                        </div>
                        {!loading &&
                            <div className='font-bold text-lg text-slate-800'>
                                ({totalItems})
                            </div>
                        }
                    </div>
                    <div className="flex items-center gap-2 mb-2">


                        <button onClick={handleRefresh} disabled={loading} className="rounded-lg bg-white border border-slate-200 text-slate-600 p-2 hover:bg-slate-50 transition-colors shadow-sm">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search events..."
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-slate-800 focus:border-[#422568] outline-none transition-all shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => setShowExportModal(true)}
                            className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-4 py-1.5 text-[11px] text-slate-800 font-bold hover:bg-slate-50 transition-all shadow-sm"
                        >
                            <Download className="w-4 h-4 text-cyan-500" />
                            Export
                        </button>
                        <LocationSelectorSection
                            ids={locationIds}
                            onIdsChange={handleLocationChange}
                            dropdownClassName="h-8 !min-h-8 py-1 text-[11px] w-32 rounded-md pl-2 pr-7"
                        />
                    </div>



                </div>




                <div className="flex items-center justify-between px-2 py-1 bg-slate-50/50 rounded-t-xl border border-b-0 border-slate-200 mb-0">
                    <div className="flex items-center gap-4">
                        {/* Status Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] select-none">Status:</span>
                            <div className="flex items-center bg-white rounded-lg p-1 border border-slate-200">
                                {(['all', 'open', 'closed'] as const).map((s) => {
                                    const isSelected = statusFilter === s;
                                    let styles = '';
                                    if (s === 'open') {
                                        styles = isSelected ? 'bg-yellow-50 text-yellow-600 font-bold' : 'text-yellow-600 hover:text-yellow-700';
                                    } else if (s === 'closed') {
                                        styles = isSelected ? 'bg-green-50 text-green-600 font-bold' : 'text-green-600 hover:text-green-700';
                                    } else {
                                        styles = isSelected ? 'bg-slate-200 text-slate-800 font-bold' : 'text-slate-500 hover:text-slate-600';
                                    }
                                    return (
                                        <button
                                            key={s}
                                            onClick={() => handleStatusFilterChange(s)}
                                            className={`px-3 py-1 rounded-md text-[10px] uppercase tracking-wider transition-all ${styles}`}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="h-4 w-px bg-slate-200" />

                        {/* Type Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] select-none">Type:</span>
                            <div className="w-44">
                                <SingleSelectLocalSearchApiDropdown
                                    icon={Tag}
                                    apiEndpoint="/events/types"
                                    displayProperty="label"
                                    idProperty="event_type"
                                    searchFields={['label', 'event_type']}
                                    value={eventTypeOption}
                                    onChange={handleEventTypeChange}
                                    placeholder="All Types"
                                    searchInputPlaceholder="Search types..."
                                    className="!p-0"
                                    buttonClassName="!py-1 !px-2 !text-[10px] !bg-white !border-slate-200 !shadow-none !h-7 !text-slate-700 font-bold"
                                />
                            </div>
                        </div>

                        <div className="h-4 w-px bg-slate-200" />

                        {/* Severity Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] select-none">Severity:</span>
                            <div className="flex items-center bg-white rounded-lg p-1 border border-slate-200">
                                {(['all', 'critical', 'warning', 'info'] as const).map((s) => {
                                    const isSelected = severityFilter === s;
                                    let styles = '';
                                    if (s === 'critical') {
                                        styles = isSelected ? 'bg-red-50 text-red-600 font-bold' : 'text-red-600 hover:text-red-700';
                                    } else if (s === 'warning') {
                                        styles = isSelected ? 'bg-yellow-50 text-yellow-600 font-bold' : 'text-yellow-600 hover:text-yellow-700';
                                    } else if (s === 'info') {
                                        styles = isSelected ? 'bg-blue-50 text-blue-600 font-bold' : 'text-blue-600 hover:text-blue-700';
                                    } else {
                                        styles = isSelected ? 'bg-slate-200 text-slate-800 font-bold' : 'text-slate-500 hover:text-slate-600';
                                    }
                                    return (
                                        <button
                                            key={s}
                                            onClick={() => handleSeverityFilterChange(s)}
                                            className={`px-3 py-1 rounded-md text-[10px] uppercase tracking-wider transition-all ${styles}`}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Date Preset Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] select-none">Date:</span>
                            <div className="flex items-center bg-white rounded-lg p-1 border border-slate-200">
                                {(['all', '24h', '7d', '30d', 'custom'] as const).map((p) => {
                                    const isSelected = datePreset === p;
                                    let styles = isSelected ? 'bg-slate-200 text-slate-800 font-bold' : 'text-slate-500 hover:text-slate-600';
                                    return (
                                        <button
                                            key={p}
                                            onClick={() => handleDatePresetChange(p)}
                                            className={`px-3 py-1 rounded-md text-[10px] uppercase tracking-wider transition-all ${styles}`}
                                        >
                                            {p === 'all' ? 'All' : p === '24h' ? '24H' : p === '7d' ? '7D' : p === '30d' ? '30D' : 'Custom'}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Date Range */}
                        {datePreset === 'custom' && (
                            <div className="flex items-center gap-1.5 bg-white rounded-lg p-1 px-3 border border-slate-200">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                <div className="flex items-center gap-1">
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => handleDateChange('from_date', e.target.value)}
                                        onClick={(e) => (e.target as any).showPicker?.()}
                                        className="bg-transparent border-none text-[10px] text-slate-700 outline-none font-bold font-mono cursor-pointer hover:text-[#422568] transition-colors w-[95px]"
                                        title="From date"
                                    />
                                    <span className="text-slate-300 text-[10px] font-bold">→</span>
                                    <input
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => handleDateChange('to_date', e.target.value)}
                                        onClick={(e) => (e.target as any).showPicker?.()}
                                        className="bg-transparent border-none text-[10px] text-slate-700 outline-none font-bold font-mono cursor-pointer hover:text-[#422568] transition-colors w-[95px]"
                                        title="To date"
                                    />
                                </div>
                                {(dateFrom || dateTo) && (
                                    <button
                                        onClick={clearDateFilters}
                                        className="ml-1 px-1.5 py-0.5 bg-rose-50 text-[9px] text-rose-600 hover:bg-rose-100 rounded font-black uppercase transition-all"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>


                {/* Table */}
                <div className="bg-white border border-slate-200 rounded-b-xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr className="border-b border-slate-200">
                                    {/* Asset / Host / Agent */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('asset_hostname')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Server size={14} className="text-cyan-600" />
                                            Asset Name
                                            <SortIcon field="asset_hostname" />
                                        </div>
                                    </th>

                                    {!level?.id && (
                                        <th className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest">
                                            <div className="flex items-center gap-2">
                                                <Activity size={14} className="text-emerald-600" />
                                                Client
                                            </div>
                                        </th>
                                    )}

                                    {/* Event Type */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('event_type')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Tag size={14} className="text-purple-600" />
                                            Type
                                            <SortIcon field="event_type" />
                                        </div>
                                    </th>

                                    {/* Description */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('description')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Info size={14} className="text-cyan-600" />
                                            Description
                                            <SortIcon field="description" />
                                        </div>
                                    </th>

                                    {/* Event Status */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('event_status')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <CircleDot size={14} className="text-emerald-600" />
                                            Status
                                            <SortIcon field="event_status" />
                                        </div>
                                    </th>

                                    {/* Severity */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('severity')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Shield size={14} className="text-red-500" />
                                            Severity
                                            <SortIcon field="severity" />
                                        </div>
                                    </th>

                                    {/* Ticket */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('ticket_number')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Ticket size={14} className="text-[#422568]" />
                                            Ticket
                                            <SortIcon field="ticket_number" />
                                        </div>
                                    </th>

                                    {/* Created */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('created')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Calendar size={14} className="text-yellow-600" />
                                            Created
                                            <SortIcon field="created" />
                                        </div>
                                    </th>

                                    {/* Actions */}
                                    <th className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest text-center">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading ? (
                                    <tr><td colSpan={!level?.id ? 9 : 8} className="py-16 text-center text-slate-500 font-bold uppercase tracking-widest italic">
                                        <div className="flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin text-[#422568]" />
                                            Loading events
                                        </div>
                                    </td></tr>
                                ) : eventsData.length === 0 ? (
                                    <tr><td colSpan={!level?.id ? 9 : 8} className="py-16 text-center text-slate-500 font-bold uppercase tracking-widest italic">
                                        <div className="flex flex-col items-center gap-2">
                                            <Activity className="w-6 h-6 text-slate-300" />
                                            No events found.
                                        </div>
                                    </td></tr>
                                ) : (
                                    eventsData.map((event) => {
                                        const sevConfig = getSeverityConfig(event.severity);
                                        const SevIcon = sevConfig.icon;
                                        const statusConfig = getStatusConfig(event.event_status);

                                        return (
                                            <tr key={event.id} onClick={() => setSelectedEvent(event)} className="hover:bg-slate-50 transition-colors group border-b border-slate-50 cursor-pointer">
                                                {/* Asset / Host / Agent */}
                                                <td className="px-3 py-3">
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-slate-800 text-[11px] font-bold leading-tight">
                                                            {event.asset_name || '-'}
                                                        </span>
                                                        {/* {event.asset_ip && (
                                                            <span className="text-slate-500 text-[9px] font-mono flex items-center gap-1">
                                                                <Network className="w-2.5 h-2.5 text-slate-400" />
                                                                {event.asset_ip}
                                                            </span>
                                                        )}
                                                        {event.agent_name && (
                                                            <span className="text-slate-500 text-[9px] flex items-center gap-1">
                                                                <Bot className="w-2.5 h-2.5 text-slate-400" />
                                                                {event.agent_name}
                                                            </span>
                                                        )} */}
                                                    </div>
                                                </td>

                                                {!level?.id && (
                                                    <td className="px-3 py-3">
                                                        <span className="text-emerald-600 font-extrabold text-[10px] uppercase">
                                                            {event.client_name || '-'}
                                                        </span>
                                                    </td>
                                                )}

                                                {/* Event Type */}
                                                <td className="px-3 py-3">
                                                    <span className="text-slate-700 font-bold text-[11px]">
                                                        {formatEventType(event.event_type)}
                                                    </span>
                                                </td>

                                                {/* Description */}
                                                <td className="px-3 py-3 max-w-[350px]">
                                                    <div className="text-slate-600 text-[10px] leading-relaxed">
                                                        {(() => {
                                                            const desc = event.description || '-';
                                                            if (desc.length <= 100) {
                                                                return <span>{desc}</span>;
                                                            }
                                                            const isExpanded = !!expandedDescriptions[event.id];
                                                            return (
                                                                <div>
                                                                    <span>
                                                                        {isExpanded ? desc : `${desc.substring(0, 100)}...`}
                                                                    </span>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleDescription(event.id);
                                                                        }}
                                                                        className="ml-1 text-[#422568] hover:text-indigo-600 font-extrabold hover:underline whitespace-nowrap"
                                                                    >
                                                                        {isExpanded ? 'Show less' : 'Show more'}
                                                                    </button>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </td>

                                                {/* Event Status */}
                                                <td className="px-3 py-3">
                                                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusConfig.bg} ${statusConfig.color} border ${statusConfig.border}`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                                                        {event.event_status || '-'}
                                                    </div>
                                                </td>

                                                {/* Severity */}
                                                <td className="px-3 py-3">
                                                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${sevConfig.bg} ${sevConfig.color} border ${sevConfig.border}`}>

                                                        <SevIcon className="w-3 h-3" />
                                                        {event.severity || 'Unknown'}
                                                    </div>
                                                </td>

                                                {/* Ticket */}
                                                <td
                                                    className="px-3 py-3"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const url = event.ticket_url || (event.ticket_id && localStorage.getItem('ticketing_domain') ? `${localStorage.getItem('ticketing_domain')}/tickets/${event.ticket_id}` : null);
                                                        if (url) window.open(url, '_blank');
                                                    }}
                                                >
                                                    {event.ticket_id || event.ticket_number ? (
                                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded border text-blue-600 bg-blue-50 border-blue-100 uppercase tracking-wider cursor-pointer shadow-sm">
                                                            <SquareArrowOutUpRight className="w-3 h-3 flex-shrink-0" />
                                                            #{event.ticket_id ?? event.ticket_number}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-300 text-[10px]">—</span>
                                                    )}
                                                </td>

                                                {/* Created */}
                                                <td className="px-3 py-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-slate-700 text-[10px] font-mono flex items-center gap-1.5">
                                                            <Clock className="w-3 h-3 text-slate-400" />
                                                            {formatTimestamp(`${event.created}z`)}
                                                        </span>
                                                        <span className="text-slate-500 text-[9px] ml-[18px]">
                                                            {getRelativeTime(`${event.created}z`)}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Actions */}
                                                <td className="px-3 py-3 text-center">
                                                    {event.event_status?.toLowerCase() === 'open' ? (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCloseEventClick(event);
                                                            }}
                                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider text-red-600 bg-red-50 border border-red-100 hover:bg-red-100 hover:text-red-700 transition-all"
                                                            title="Close Event"
                                                        >
                                                            <XCircle className="w-3.5 h-3.5" />
                                                            Close
                                                        </button>
                                                    ) : (
                                                        <span className="text-slate-300 text-[10px]">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>


                    {/* Pagination */}
                    <div className="bg-slate-50/50 border-t border-slate-200 px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown value={itemsPerPage} onChange={handleItemsPerPageChange} />
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                                <span className="text-slate-800">{totalItems > 0 ? startIndex + 1 : 0}</span> - <span className="text-slate-800">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                            <div className="px-3 py-1 bg-[#422568] rounded-lg text-white text-[11px] font-extrabold mx-1.5">{currentPage}</div>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Event Detail Slide-out Panel */}
            <AnimatePresence>
                {selectedEvent && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedEvent(null)}
                            className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className="fixed right-0 top-0 h-full w-[480px] bg-white border-l border-slate-200 z-50 flex flex-col shadow-2xl"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 shadow-sm">
                                        <Activity className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-black text-slate-800 leading-tight">{selectedEvent.asset_name}</p>
                                        {/* <p className="text-[10px] text-slate-500 font-mono font-bold tracking-tight">{selectedEvent.asset_ip}</p> */}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-md border uppercase tracking-wider shadow-sm ${selectedEvent.event_type === 'camera_offline' ? 'text-rose-600 bg-rose-50 border-rose-100'
                                        : selectedEvent.event_type === 'camera_online' ? 'text-emerald-600 bg-emerald-50 border-emerald-100'
                                            : 'text-indigo-600 bg-indigo-50 border-indigo-100'
                                        } `}>
                                        {selectedEvent.event_type.replace(/_/g, ' ')}
                                    </span>
                                    <button
                                        onClick={() => setSelectedEvent(null)}
                                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-all font-bold"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Event Details Content */}
                            <div className="flex-1 min-h-0 overflow-y-auto custom-scroll p-5 flex flex-col gap-4 pb-20">
                                {/* Badges & Time Row */}
                                <div className="flex items-center justify-between gap-2 flex-wrap bg-slate-50/50 p-2 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-2">
                                        {selectedEvent.event_status && (
                                            <span className={`text-[9px] font-black px-2 py-0.5 rounded-md border uppercase tracking-widest shadow-sm ${selectedEvent.event_status === 'closed' ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-amber-600 bg-amber-50 border-amber-100'}`}>
                                                {selectedEvent.event_status}
                                            </span>
                                        )}
                                        {(selectedEvent.ticket_id || selectedEvent.ticket_number || selectedEvent.ticket_url) && (
                                            <span
                                                className="inline-flex items-center gap-1.5 text-[9px] font-black px-2 py-0.5 rounded-md border text-blue-600 bg-blue-50 border-blue-100 uppercase tracking-widest cursor-pointer shadow-sm hover:bg-blue-100 transition-colors"
                                                onClick={() => {
                                                    const url = selectedEvent.ticket_url || (selectedEvent.ticket_id && localStorage.getItem('ticketing_domain') ? `${localStorage.getItem('ticketing_domain')}/tickets/${selectedEvent.ticket_id}` : null);
                                                    if (url) window.open(url, '_blank');
                                                }}
                                            >
                                                <SquareArrowOutUpRight className="w-3 h-3 flex-shrink-0" />
                                                #{selectedEvent.ticket_id ?? selectedEvent.ticket_number}
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-[9px] text-slate-400 font-bold flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {new Date(`${selectedEvent.created}Z`).toLocaleString()}
                                    </span>
                                </div>

                                {/* Details Grid */}
                                <div className="grid grid-cols-1 gap-3">
                                    <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1.5 flex items-center gap-2">
                                            <Info className="w-3 h-3" />
                                            Message
                                        </p>
                                        <p className="text-xs text-slate-700 font-bold leading-relaxed">{selectedEvent.message}</p>
                                    </div>

                                    <div className="grid grid-cols-1 gap-3">
                                        {/* <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1.5">Description</p>
                                            <p className="text-xs text-slate-600 font-medium leading-relaxed line-clamp-3">{selectedEvent.description || 'N/A'}</p>
                                        </div> */}
                                        <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1.5 flex items-center gap-2">
                                                <Bot className="w-3 h-3" />
                                                Agent
                                            </p>
                                            <p className="text-xs text-slate-700 font-bold truncate" title={selectedEvent.agent_name}>{selectedEvent.agent_name}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline Section */}
                                {selectedEvent.event_data && (
                                    <div className="rounded-xl border border-slate-100 bg-slate-50/20 p-4 mt-1">
                                        <p className="text-[10px] font-black text-slate-800 uppercase tracking-[0.2em] mb-4 border-b border-slate-100 pb-2 flex items-center gap-2">
                                            <Activity className="w-3.5 h-3.5 text-[#422568]" />
                                            Log History
                                        </p>
                                        {(() => {
                                            try {
                                                let parsedData = JSON.parse(selectedEvent.event_data);
                                                if (typeof parsedData === 'string') parsedData = JSON.parse(parsedData);
                                                const timelineEvents = Array.isArray(parsedData) ? parsedData : (typeof parsedData === 'object' && parsedData !== null ? [parsedData] : []);

                                                if (timelineEvents.length === 0) {
                                                    return (
                                                        <pre className="text-[10px] text-slate-500 font-mono leading-relaxed whitespace-pre-wrap break-all bg-slate-50 p-4 rounded-xl border border-slate-100 font-bold">
                                                            {selectedEvent.event_data}
                                                        </pre>
                                                    );
                                                }

                                                return (
                                                    <div className="relative border-l-2 border-slate-100 ml-2 pl-5 py-1 space-y-4">
                                                        {timelineEvents.map((evtLog: any, idx: number) => {
                                                            const d = evtLog.data || {};
                                                            const status = evtLog.status || 'info';
                                                            const isClosed = status.toLowerCase() === 'closed';
                                                            const isOpen = status.toLowerCase() === 'open';

                                                            const badgeColor = isClosed ? 'text-emerald-600 bg-emerald-50 border border-emerald-100'
                                                                : isOpen ? 'text-rose-600 bg-rose-50 border border-rose-100'
                                                                    : 'text-amber-600 bg-amber-50 border border-amber-100';

                                                            const dotColor = isClosed ? 'bg-emerald-500 ring-white shadow-emerald-200'
                                                                : isOpen ? 'bg-rose-500 ring-white shadow-rose-200 animate-pulse'
                                                                    : 'bg-amber-500 ring-white shadow-amber-200';

                                                            return (
                                                                <div key={idx} className="relative group">
                                                                    {/* Timeline Dot */}
                                                                    <div className="absolute -left-[30px] top-3 flex items-center justify-center z-10 transition-transform group-hover:scale-125">
                                                                        <div className={`w-2.5 h-2.5 rounded-full ring-4 shadow-lg ${dotColor}`} />
                                                                    </div>

                                                                    {/* Card Content */}
                                                                    <div className={`bg-white rounded-xl border p-3 transition-all duration-300 shadow-sm ${isClosed ? 'border-emerald-50 hover:border-emerald-100 hover:bg-emerald-50/20'
                                                                        : isOpen ? 'border-rose-50 hover:border-rose-100 hover:bg-rose-50/20'
                                                                            : 'border-slate-50 hover:border-slate-100 hover:bg-slate-50/40'
                                                                        }`}>

                                                                        <div className="flex items-center justify-between mb-2 border-b border-slate-50 pb-1.5">
                                                                            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-widest ${badgeColor}`}>
                                                                                {status}
                                                                            </span>
                                                                            <span className="text-[8px] text-slate-400 font-mono font-bold bg-slate-50 px-1.5 py-0.5 rounded-md border border-slate-100">
                                                                                {evtLog.created ? new Date(`${evtLog.created}${!evtLog.created.endsWith('Z') && !evtLog.created.includes('+') ? 'Z' : ''}`).toLocaleString() : 'N/A'}
                                                                            </span>
                                                                        </div>

                                                                        <p className="text-[10px] text-slate-700 font-bold leading-relaxed mb-2">
                                                                            {isOpen ? 'Incident opened.' : isClosed ? 'Incident resolved.' : status}
                                                                        </p>

                                                                        {/* Key Metrics Grid */}
                                                                        {Object.keys(d).length > 0 && (
                                                                            <div className="bg-slate-50/50 rounded-lg border border-slate-100 p-2 grid grid-cols-2 gap-2">
                                                                                {Object.entries(d)
                                                                                    .filter(([k]) => !['ticket_description_html', 'warning_issues', 'critical_issues', 'assets'].includes(k))
                                                                                    .map(([k, v]) => {
                                                                                        const isSnapshotUrl = k.toLowerCase() === 'snapshot_url';
                                                                                        const isIntrusion = selectedEvent.event_type?.toLowerCase() === 'intrusion_detection';
                                                                                        const isTampering = selectedEvent.event_type?.toLowerCase() === 'camera_tampering';
                                                                                        const isCrowdDetection = selectedEvent.event_type?.toLowerCase() === 'crowd_detection';
                                                                                        const hasSnapshotSupport = isIntrusion || isTampering || isCrowdDetection;
                                                                                        if (hasSnapshotSupport && isSnapshotUrl && v) {
                                                                                            return (
                                                                                                <div className="flex flex-col" key={k}>
                                                                                                    <span className="text-[8px] text-slate-400 font-black uppercase tracking-tight">Snapshot</span>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => setActiveSnapshotUrl(String(v))}
                                                                                                        className="mt-1 p-1 text-indigo-500 hover:text-indigo-600 transition-all hover:scale-110 cursor-pointer w-fit self-start"
                                                                                                        title="View Snapshot"
                                                                                                    >
                                                                                                        <Eye className="w-4 h-4" />
                                                                                                    </button>
                                                                                                </div>
                                                                                            );
                                                                                        }
                                                                                        const strVal = String(v);
                                                                                        const isTimestamp = k.toLowerCase().endsWith('_at') ||
                                                                                            k.toLowerCase() === 'online_again' ||
                                                                                            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(strVal);
                                                                                        if (isTimestamp && v) {
                                                                                            const dateVal = new Date(strVal.endsWith('Z') || strVal.includes('+') ? strVal : `${strVal}Z`);
                                                                                            const formattedVal = isNaN(dateVal.getTime()) ? strVal : dateVal.toLocaleString();
                                                                                            return (
                                                                                                <div className="flex flex-col" key={k}>
                                                                                                    <span className="text-[8px] text-slate-400 font-black uppercase tracking-tight">{k.replace(/_/g, ' ')}</span>
                                                                                                    <span className="text-[10px] text-slate-800 font-bold mt-0.5 break-all">
                                                                                                        {formattedVal}
                                                                                                    </span>
                                                                                                </div>
                                                                                            );
                                                                                        }
                                                                                        return (
                                                                                            <div className="flex flex-col" key={k}>
                                                                                                <span className="text-[8px] text-slate-400 font-black uppercase tracking-tight">{k.replace(/_/g, ' ')}</span>
                                                                                                <span className="text-[10px] text-slate-800 font-bold mt-0.5 break-all">
                                                                                                    {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}
                                                                                                </span>
                                                                                            </div>
                                                                                        );
                                                                                    })
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            } catch (e) {
                                                return <pre className="text-[10px] text-slate-500 bg-slate-50 p-4 rounded-xl">{selectedEvent.event_data}</pre>;
                                            }
                                        })()}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Close Event Modal */}
            {closeModalOpen && createPortal(
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => { if (!closingEvent) setCloseModalOpen(false); }}>
                    <div
                        className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-50 rounded-xl shadow-sm">
                                    <XCircle className="w-5 h-5 text-red-500" />
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-slate-800 tracking-tight">Close Event</h3>
                                    <p className="text-xs text-slate-500 font-bold truncate max-w-[200px]">{closeEventName}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { if (!closingEvent) setCloseModalOpen(false); }}
                                disabled={closingEvent}
                                className="text-slate-400 hover:text-slate-800 transition-all bg-white p-2 rounded-xl border border-slate-100 hover:border-slate-200 shadow-sm disabled:opacity-50"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-5">
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Reason for closing</label>
                                <textarea
                                    value={closeReason}
                                    onChange={(e) => setCloseReason(e.target.value)}
                                    placeholder="Enter the reason for closing this event..."
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3 text-sm text-slate-800 focus:border-[#422568] focus:bg-white outline-none transition-all placeholder:text-slate-300 resize-none h-32 font-bold shadow-inner"
                                    disabled={closingEvent}
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => { if (!closingEvent) setCloseModalOpen(false); }}
                                    disabled={closingEvent}
                                    className="flex-1 px-4 py-3 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-2xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmCloseEvent}
                                    disabled={closingEvent || !closeReason.trim()}
                                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-red-100"
                                >
                                    {closingEvent ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Process...
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-4 h-4" />
                                            Submit
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Snapshot Viewer Modal */}
            {activeSnapshotUrl && createPortal(
                <div
                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[9999] flex items-center justify-center p-6 animate-in fade-in duration-200"
                    onClick={() => setActiveSnapshotUrl(null)}
                >
                    <div
                        className="relative bg-white border border-slate-200 rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-50 rounded-xl shadow-sm text-indigo-600">
                                    <Eye className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-slate-800 tracking-tight">Snapshot</h3>
                                    <p className="text-xs text-slate-400 font-medium font-mono truncate max-w-[280px] sm:max-w-md" title={activeSnapshotUrl}>
                                        {activeSnapshotUrl}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setActiveSnapshotUrl(null)}
                                className="text-slate-400 hover:text-slate-800 transition-all bg-white p-2 rounded-xl border border-slate-100 hover:border-slate-200 shadow-sm"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-4 bg-slate-950 flex items-center justify-center min-h-[300px] max-h-[70vh] overflow-hidden">
                            <img
                                src={activeSnapshotUrl}
                                alt="Intrusion Detection Snapshot"
                                className="max-w-full max-h-[65vh] object-contain rounded-lg shadow-md hover:scale-[1.01] transition-transform duration-300"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=Snapshot+Not+Available';
                                }}
                            />
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                            <a
                                href={activeSnapshotUrl}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className="px-4 py-2.5 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-sm"
                            >
                                <Download className="w-4 h-4" />
                                Open In New Tab
                            </a>
                            <button
                                onClick={() => setActiveSnapshotUrl(null)}
                                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg shadow-indigo-100"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <AgentExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                templateName="events"
                clientId={clientId || level?.id}
                filters={{
                    ...(statusFilter !== 'all' && { event_status: statusFilter }),
                    ...(eventTypeOption?.event_type && { event_type: eventTypeOption.event_type }),
                    ...(severityFilter !== 'all' && { severity: severityFilter }),
                    ...(locationIds.regionId && { region_id: locationIds.regionId }),
                    ...(locationIds.stateId && { state_id: locationIds.stateId }),
                    ...(locationIds.districtId && { district_id: locationIds.districtId }),
                    ...(locationIds.townId && { town_id: locationIds.townId }),
                    ...(debouncedSearch.trim() && { search: debouncedSearch.trim() })
                }}
            />
        </div>
    );
};


export default EventsTable;

